import type { CivilDate } from '@hue/design-system';
import { useNavigate } from '@tanstack/react-router';
import { z } from 'zod';

/// O recorte de uma lista — filtro, ordenação e página — vive na URL, e não em `useState`.
///
/// Antes disto, recarregar a página apagava o recorte que a pessoa montou, e não havia como mandar
/// "olha essa linha" para ninguém: só um roteiro de cliques. O preço de pôr na URL é ela virar
/// ilegível se todo valor for escrito nela, então **o que é padrão não aparece**: `/audit` é a
/// auditoria recém-aberta, e `/audit?action=membership.invited` é a filtrada.

/// Os pedaços que repetem em toda lista. Todos usam `.catch`: parâmetro estragado na barra de
/// endereço — link cortado, alguém editando à mão — cai no padrão em vez de derrubar a tela. Uma
/// URL malformada não pode ser mais grave do que não ter URL nenhuma, que era o estado anterior.
export const sortDirectionSearch = z.enum(['asc', 'desc']).optional().catch(undefined);

export const civilDateSearch = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/u)
  .optional()
  .catch(undefined) as z.ZodType<CivilDate | undefined>;

/// Filtro de escolha múltipla na URL. O link de quando o filtro era único ainda abre: `ALL` é a
/// lista vazia (todos), um valor só vira lista de um e um valor antigo pode ter tradução própria.
/// Valor desconhecido sai da lista em vez de apagar o filtro inteiro.
export function choicesSearch<const T extends readonly [string, ...string[]]>(
  values: T,
  legacy: Readonly<Record<string, ReadonlyArray<T[number]>>> = {},
) {
  const known = new Set<string>(values);
  return z
    .preprocess(
      (raw) => {
        if (raw === undefined) return undefined;
        const list: unknown[] = Array.isArray(raw) ? raw : [raw];
        const chosen = list.flatMap((item) =>
          typeof item !== 'string' ? [] : (legacy[item] ?? (item === 'ALL' ? [] : [item])),
        );
        return [...new Set(chosen)].filter((item) => known.has(item));
      },
      z.array(z.enum(values)).optional(),
    )
    .catch(undefined) as z.ZodType<Array<T[number]> | undefined>;
}

/// Escolhas cujo catálogo vem do servidor (tipos de e-mail, ações e registros).
export function stringChoicesSearch(item: z.ZodString = z.string().min(1).max(160), max = 50) {
  return z
    .preprocess((raw) => {
      if (raw === undefined) return undefined;
      const values: unknown[] = Array.isArray(raw) ? raw : [raw];
      return [
        ...new Set(
          values.filter((value): value is string => typeof value === 'string' && value !== 'ALL'),
        ),
      ];
    }, z.array(item).max(max).optional())
    .catch(undefined) as z.ZodType<string[] | undefined>;
}

/// O teto existe para a URL não virar um pedido de dez mil linhas ao servidor.
export const pageSizeSearch = z.coerce.number().int().min(1).max(100).optional().catch(undefined);

export const pageOffsetSearch = z.coerce.number().int().min(0).optional().catch(undefined);

/// Um recorte parcial. Não é `Partial<T>` porque com `exactOptionalPropertyTypes` os dois deixam
/// de ser a mesma coisa: aqui a chave pode vir presente **e** valendo `undefined`, que é justamente
/// como o parâmetro ausente chega da URL e como um filtro é apagado.
export type ViewPatch<T> = { [K in keyof T]?: T[K] | undefined };

export type ViewSetter<T> = (patch: ViewPatch<T>, options?: { replace?: boolean }) => void;

function sameValue(a: unknown, b: unknown): boolean {
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((item, at) => item === b[at]);
  }
  return a === b;
}

/// O que é igual ao padrão sai da URL. `undefined` também sai — é assim que o roteador apaga um
/// parâmetro.
export function stripDefaults<T extends Record<string, unknown>>(
  value: T,
  defaults: T,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, current] of Object.entries(value)) {
    out[key] = sameValue(current, defaults[key]) ? undefined : current;
  }
  return out;
}

/// Aplica um recorte parcial sobre o atual.
///
/// **Mexer em qualquer coisa que não seja a página volta para a primeira.** Trocar o filtro estando
/// na página 7 mostrava uma lista vazia, e parecia que o filtro não tinha achado nada.
export function applyPatch<T extends Record<string, unknown>>(
  view: T,
  patch: ViewPatch<T>,
  defaults: T,
): T {
  const next = { ...view, ...patch } as Record<string, unknown>;
  if ('offset' in next && !('offset' in patch)) next['offset'] = defaults['offset'];
  return next as T;
}

/// Devolve o recorte completo (padrões preenchidos) e a função que o muda.
///
/// Além da regra da página, uma segunda mora aqui: **quem digita não empilha histórico**. O campo
/// de busca não tem espera, então cada letra é uma mudança; sem `replace`, o gesto de voltar
/// andaria letra por letra para trás.
///
/// E **mudar o recorte não mexe na rolagem**: é a mesma tela, só com outro filtro, outra visão ou
/// outro período. O padrão do roteador leva ao topo a cada navegação, o que obrigava a rolar de
/// novo até o calendário a cada clique em Semana, Próximo ou num filtro.
export function useViewState<T extends Record<string, unknown>>(
  search: ViewPatch<T>,
  defaults: T,
): [T, ViewSetter<T>] {
  const navigate = useNavigate();
  /// Chave ausente **e** chave com `undefined` querem dizer a mesma coisa — "está no padrão" —,
  /// porque é assim que o parâmetro sai da URL. Um espalhamento simples não serviria: ele deixaria
  /// o `undefined` por cima do padrão.
  const view = { ...defaults };
  for (const [key, value] of Object.entries(search)) {
    if (value !== undefined) (view as Record<string, unknown>)[key] = value;
  }

  const setView: ViewSetter<T> = (patch, options = {}) => {
    void navigate({
      to: '.',
      search: stripDefaults(applyPatch(view, patch, defaults), defaults),
      replace: options.replace ?? false,
      resetScroll: false,
    });
  };

  return [view, setView];
}
